import { useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { RingGauge, Sparkline } from '@bloo-team/ui'
import { PageStrip } from '@bloo-team/core-shell'
import { TimelineChart } from '@bloo-team/core-shell'
import { TopPanel } from '../components/TopPanel'
import { humanizeSecs } from '../lib/overview'
import { useCmStats } from '../lib/case-mgmt'
import { openCaseCount } from '../home/home-data'
import { useHunts } from '../lib/hunt-store'
import { navPath } from '@bloo-team/core-shell'
import { usePostureStats } from '../lib/overview'
import { signalMetrics } from '../lib/signal-model'
import { useTriage } from '../lib/signal-store'
import {
  sliceMsForWindow,
  useSignals,
  useSignalTimeslice,
  WINDOW_OPTIONS,
  type SignalWindow,
} from '../lib/signals'

/**
 * Vantage · Overview — the detection-posture dashboard (docs/IA.md IA8:
 * "posture cards — ingest health, open cases, signal trend"), read in funnel
 * order: posture tiles, the signal trend for the selected window, then the
 * top lists. Viewer-visible (nav marks Overview INCLUDING_VIEWER) — the page
 * is strictly read-only, no mutating control anywhere.
 *
 * Data joins (docs/overview.md):
 *   - `cases/stats` posture counters + top lists (lib/overview usePostureStats)
 *   - open cases: opened in the window and still open — every V2 status
 *     category but closed (useCmStats: one case-metrics read for the window)
 *   - `signals/timeslice_data` buckets (useSignalTimeslice; demo = fixture)
 *   - local triage FP rate (signalMetrics over useSignals 7d + useTriage)
 *   - local hunt store active count (useHunts)
 *
 * Top detections / suspects / targets link to /vantage/signals PLAIN: the
 * signals page keeps its filters as component state (only the legacy `?wbId=`
 * deep-link exists, and `cases/stats` rows carry names, not workbook ids).
 */

export interface OverviewSearch {
  window?: SignalWindow
}

const isWindow = (v: unknown): v is SignalWindow =>
  typeof v === 'string' && (WINDOW_OPTIONS as readonly string[]).includes(v)

/** Route-shim search validation — the composer's `validateSearch`. */
export function validateOverviewSearch(search: Record<string, unknown>): OverviewSearch {
  return {
    ...(isWindow(search.window) ? { window: search.window } : {}),
  }
}

/** '—' until the query lands; locale-grouped after. */
function fmtCount(n: number | undefined): string {
  return n === undefined ? '—' : n.toLocaleString('en-US')
}

/**
 * percent_diff → hud-delta. `goodUp` says whether a rise is good news
 * (detections discovered: yes; signals/cases/suspects/targets: no).
 */
function Delta({ pct, goodUp }: { pct: number | null; goodUp: boolean }) {
  if (pct === null || !Number.isFinite(pct)) return null
  const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat'
  const mark = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–'
  const abs = Math.abs(pct)
  return (
    <span className="hud-delta" data-dir={dir} data-good={String(dir === 'flat' ? true : dir === 'up' ? goodUp : !goodUp)}>
      {mark} {abs >= 10 ? Math.round(abs) : Math.round(abs * 10) / 10}%
    </span>
  )
}

export interface OverviewPageProps {
  clusterId: string
  scopeId: string
  search: OverviewSearch
}

export function OverviewPage({ clusterId, scopeId, search }: OverviewPageProps) {
  const navigate = useNavigate()
  const scope = useMemo(() => ({ clusterId, scopeId }), [clusterId, scopeId])
  const window: SignalWindow = search.window ?? '7d'
  const p = (to: string) => navPath(clusterId, scopeId, to)

  const postureQ = usePostureStats(scope, window)
  const caseStatsQ = useCmStats(scope, window)
  const timesliceQ = useSignalTimeslice(scope, window)
  /** FP rate is a fixed 7d measure — the same query key the signals page uses. */
  const signals7dQ = useSignals(scope, '7d')
  const { triage } = useTriage(clusterId, scopeId)
  const { hunts } = useHunts(clusterId, scopeId)

  const posture = postureQ.data
  const buckets = useMemo(() => timesliceQ.data ?? [], [timesliceQ.data])
  const trend = useMemo(() => buckets.map((b) => b.count), [buckets])
  const fpRate = useMemo(() => signalMetrics(signals7dQ.signals, triage).fpRate, [signals7dQ.signals, triage])
  const activeHunts = hunts.filter((h) => h.status === 'active').length

  /** Every status category but closed. */
  const openCases = openCaseCount(caseStatsQ.data?.counts) ?? undefined

  const sevRows = posture?.severity ?? []
  const sevMax = sevRows.reduce((m, r) => Math.max(m, r.value), 1)

  const setWindow = (w: SignalWindow) =>
    void navigate({
      to: '/$clusterId/$scopeId/vantage/overview',
      params: { clusterId, scopeId },
      search: { window: w },
      replace: true,
    })

  const openSignalsPage = () =>
    void navigate({
      to: '/$clusterId/$scopeId/vantage/signals',
      params: { clusterId, scopeId },
      search: { window },
    })

  return (
    <div>
      <PageStrip
        title="Overview"
        meta={`scope ${scopeId}`}
        end={
          <div className="bui-toggles" role="group" aria-label="Window">
            {WINDOW_OPTIONS.map((w) => (
              <button key={w} type="button" className="bui-toggle" aria-pressed={window === w} onClick={() => setWindow(w)}>
                {w}
              </button>
            ))}
          </div>
        }
      />

      <div className="hud-grid">
        {postureQ.isError ? (
          <div className="hud-span-12">
            <div className="bui-alert bui-alert-error" role="alert">
              <span className="bui-alert-mark">✕</span>
              <span>
                <span className="bui-alert-title">Posture stats failed to load</span>
                {postureQ.error instanceof Error ? postureQ.error.message : 'unknown error'}
              </span>
            </div>
          </div>
        ) : null}

        {/* Row 1 — posture tiles, funnel order */}
        <Link to={p('/vantage/signals')} className="hud-panel hud-span-3">
          <div className="hud-stack">
            <div className="hud-row hud-row-between">
              <span className="hud-eyebrow">
                <b>V</b> · signals · {window}
              </span>
              <Delta pct={posture?.signals.percentDiff ?? null} goodUp={false} />
            </div>
            <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-2)' }}>
              <span className="hud-value">{fmtCount(posture?.signals.count)}</span>
              <span className="hud-unit">new</span>
            </div>
            {trend.length >= 2 ? <Sparkline data={trend} width={200} height={24} /> : null}
          </div>
        </Link>

        <Link to={p('/vantage/cases')} className="hud-panel hud-span-3">
          <div className="hud-stack">
            <div className="hud-row hud-row-between">
              <span className="hud-eyebrow">
                <b>V</b> · open cases
              </span>
              <Delta pct={posture?.cases.percentDiff ?? null} goodUp={false} />
            </div>
            <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-2)' }}>
              <span className="hud-value">{fmtCount(openCases)}</span>
              <span className="hud-unit">still open</span>
            </div>
            <span className="hud-unit">
              {fmtCount(caseStatsQ.data?.created)} opened in {window}
            </span>
          </div>
        </Link>

        <Link to={p('/vantage/detections')} className="hud-panel hud-span-3">
          <div className="hud-stack">
            <div className="hud-row hud-row-between">
              <span className="hud-eyebrow">
                <b>V</b> · detections
              </span>
              <Delta pct={posture?.detections.percentDiff ?? null} goodUp />
            </div>
            <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-2)' }}>
              <span className="hud-value">{fmtCount(posture?.detections.count)}</span>
              <span className="hud-unit">unique firing</span>
            </div>
          </div>
        </Link>

        <div className="hud-panel hud-span-3">
          <div className="hud-stack">
            <div className="hud-row hud-row-between">
              <span className="hud-eyebrow">
                <b>V</b> · suspects / targets
              </span>
              <Delta pct={posture?.suspects.percentDiff ?? null} goodUp={false} />
            </div>
            <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-4)' }}>
              <span>
                <span className="hud-value">{fmtCount(posture?.suspects.count)}</span>{' '}
                <span className="hud-unit">suspects</span>
              </span>
              <span>
                <span className="hud-value">{fmtCount(posture?.targets.count)}</span>{' '}
                <span className="hud-unit">targets</span>
              </span>
            </div>
          </div>
        </div>

        <div className="hud-panel hud-span-4">
          <div className="hud-stack">
            <span className="hud-eyebrow">
              <b>V</b> · mttd
            </span>
            <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-2)' }}>
              <span className="hud-value">{posture ? humanizeSecs(posture.mttdSeconds) : '—'}</span>
              <span className="hud-unit">mean time to detect</span>
            </div>
          </div>
        </div>

        <div className="hud-panel hud-span-4">
          <div className="hud-stack">
            <span className="hud-eyebrow">
              <b>V</b> · fp rate · 7d
            </span>
            <div className="hud-row" style={{ gap: 'var(--bloo-space-4)' }}>
              <RingGauge value={fpRate ?? 0} size={56} color="var(--bui-warn)">
                <span className="hud-unit">{fpRate === null ? '—' : `${Math.round(fpRate * 100)}%`}</span>
              </RingGauge>
              <span className="hud-unit">closed as false positive (local triage)</span>
            </div>
          </div>
        </div>

        <Link to={p('/vantage/hunts')} className="hud-panel hud-span-4">
          <div className="hud-stack">
            <span className="hud-eyebrow">
              <b>V</b> · active hunts
            </span>
            <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-2)' }}>
              <span className="hud-value">{activeHunts}</span>
              <span className="hud-unit">of {hunts.length} total</span>
            </div>
          </div>
        </Link>

        {/* Row 2 — signal trend + severity split */}
        <div className="hud-panel hud-span-8">
          <div className="hud-stack">
            <div className="hud-row hud-row-between">
              <span className="hud-eyebrow">
                <b>V</b> · signal trend · {window}
              </span>
              <span className="hud-unit">drag opens signals</span>
            </div>
            {timesliceQ.isLoading ? (
              <p className="hud-unit" role="status">
                Loading trend…
              </p>
            ) : timesliceQ.isError ? (
              <div className="bui-alert bui-alert-error" role="alert">
                <span className="bui-alert-mark">✕</span>
                <span>
                  <span className="bui-alert-title">Trend failed to load</span>
                  {timesliceQ.error instanceof Error ? timesliceQ.error.message : 'unknown error'}
                </span>
              </div>
            ) : buckets.length === 0 ? (
              <p className="hud-unit" role="status">
                No signals in the last {window}.
              </p>
            ) : (
              <TimelineChart buckets={buckets} sliceMs={sliceMsForWindow(window)} style="bars" onSelectRange={openSignalsPage} />
            )}
          </div>
        </div>

        <div className="hud-panel hud-span-4">
          <div className="hud-stack">
            <span className="hud-eyebrow">
              <b>V</b> · by severity · {window}
            </span>
            {sevRows.length === 0 ? (
              <p className="hud-unit" role="status">
                {postureQ.isLoading ? 'Loading…' : 'No signals in this window.'}
              </p>
            ) : (
              <table className="topn" aria-label="Signals by severity">
                <tbody>
                  {sevRows.map((r) => {
                    const sev = r.severity.toLowerCase()
                    return (
                      /* data-severity resolves --bui-sev so the bar wears the
                         severity token; unknown labels fall back to accent. */
                      <tr key={r.severity} data-severity={sev}>
                        <td className="lbl">
                          <span className="sg-sev-dot" data-severity={sev} /> {sev}
                          <span
                            className="bar"
                            style={{ width: `${(r.value / sevMax) * 100}%`, background: 'var(--bui-sev, var(--bui-accent))' }}
                          />
                        </td>
                        <td className="n">{r.value.toLocaleString('en-US')}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Row 3 — top lists (shared-max count bars) */}
        <TopPanel
          label="top detections"
          to={p('/vantage/signals')}
          rows={posture?.topDetections ?? []}
          loading={postureQ.isLoading}
          emptyText="No detections fired in this window."
        />
        <TopPanel
          label="top tactics"
          to={p('/vantage/coverage')}
          rows={posture?.topTactics ?? []}
          loading={postureQ.isLoading}
          emptyText="No mapped tactics in this window."
        />
        <TopPanel
          label="top suspects"
          to={p('/vantage/signals')}
          rows={posture?.topSuspects ?? []}
          loading={postureQ.isLoading}
          emptyText="No suspect entities in this window."
        />
        <TopPanel
          label="top targets"
          to={p('/vantage/signals')}
          rows={posture?.topTargets ?? []}
          loading={postureQ.isLoading}
          emptyText="No target entities in this window."
        />

        <TopPanel
          label="stream distribution"
          to={p('/fabric/streams')}
          rows={(posture?.streams ?? []).map((s) => ({ name: s.stream, count: s.value }))}
          loading={postureQ.isLoading}
          emptyText="No streams produced signals in this window."
          span="hud-span-6"
        />
        <TopPanel
          label="top techniques"
          to={p('/vantage/coverage')}
          rows={posture?.topTechniques ?? []}
          loading={postureQ.isLoading}
          emptyText="No mapped techniques in this window."
          span="hud-span-6"
        />
      </div>
    </div>
  )
}
